import { useId } from 'react';
import { Link } from 'react-router-dom';
import { ImageOutlined, Refresh, Visibility } from '@mui/icons-material';
import { CircularProgress, IconButton, Skeleton, Tooltip } from '@mui/material';

import { Badge, GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { formatDate } from '@/utils';
import { useCurrentTrackingIcon } from '../hooks';
import {
  TRACKING_ICON_ANCHOR_LABELS,
  getTrackingIconAnchor,
  TrackingIconSlot,
} from '../models';
import { trackingIconPath } from '../models/TrackingIconForm';
import TrackingIconImage from './TrackingIconImage';

interface CurrentTrackingIconCardProps {
  slot: TrackingIconSlot;
}

const CurrentTrackingIconCard = ({ slot }: CurrentTrackingIconCardProps) => {
  const titleId = useId();
  const { currentIcon, isLoading, isFetching, error, refetch } =
    useCurrentTrackingIcon(slot);
  const isUnconfigured = currentIcon?.id === null;
  const isDefault = currentIcon?.isDefault === true;
  const isInitialLoading = isLoading || (isFetching && !currentIcon && !error);

  return (
    <section
      aria-labelledby={titleId}
      className='flex items-start gap-4 rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:gap-5'
    >
      {isInitialLoading ? (
        <Skeleton
          variant='rounded'
          width={88}
          height={88}
          sx={{ flexShrink: 0 }}
        />
      ) : currentIcon && !error ? (
        <div className='flex h-[88px] w-[88px] shrink-0 items-center justify-center'>
          {isUnconfigured ? (
            <ImageOutlined className='text-gray-400' sx={{ fontSize: 48 }} />
          ) : (
            <TrackingIconImage
              imageUrl={currentIcon.imageUrl}
              name={currentIcon.name}
              size={88}
            />
          )}
        </div>
      ) : null}
      <div className='flex min-w-0 flex-1 flex-col gap-2'>
        <div className='flex items-start justify-between gap-3'>
          <h2 id={titleId} className='text-sm font-medium text-gray-500'>
            Ícono que ven los clientes para{' '}
            {slot === 'destination' ? 'destino' : 'repartidor'} ahora mismo
          </h2>
          {currentIcon && !error && (
            <div className='flex shrink-0 items-center gap-1'>
              {currentIcon.id && (
                <Tooltip title='Ver información del ícono'>
                  <IconButton
                    component={Link}
                    to={`${trackingIconPath(slot)}/${currentIcon.id}`}
                    aria-label={`Ver ícono ${currentIcon.name}`}
                    size='small'
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title='Actualizar información del ícono'>
                <span className='shrink-0'>
                  <IconButton
                    aria-label='Actualizar información del ícono'
                    disabled={isFetching}
                    onClick={() => void refetch()}
                    size='small'
                  >
                    {isFetching ? (
                      <CircularProgress
                        size={20}
                        aria-label='Actualizando ícono'
                      />
                    ) : (
                      <Refresh />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )}
        </div>
        {isInitialLoading ? (
          <div
            role='status'
            aria-label='Cargando el ícono que ven los clientes'
          >
            <Skeleton width='70%' height={28} />
            <Skeleton width='55%' />
            <Skeleton width='45%' />
          </div>
        ) : error || !currentIcon ? (
          <div
            role='alert'
            className='flex flex-wrap items-center justify-between gap-3'
          >
            <p className='text-sm text-gray-600'>
              No se pudo cargar el ícono que ven los clientes.
            </p>
            <GlobalButton
              variant={GlobalButtonVariant.secondaryButton}
              action={() => void refetch()}
              disabled={isFetching}
            >
              Reintentar
            </GlobalButton>
          </div>
        ) : (
          <>
            <p className='min-w-0 text-base font-medium text-gray-900 [overflow-wrap:anywhere]'>
              {isUnconfigured ? 'Sin ícono configurado' : currentIcon.name}
            </p>
            {!isUnconfigured && (
              <div className='self-start'>
                <Badge
                  value={isDefault ? 'Predeterminado' : 'Especial'}
                  backgroundColor={isDefault ? '#F3F4F6' : '#E8F5E9'}
                  textColor={isDefault ? '#4B5563' : '#2E7D32'}
                  showBall={false}
                  density='compact'
                />
              </div>
            )}
            {!isUnconfigured && (
              <p className='text-sm text-gray-600'>
                Posición en el mapa:{' '}
                {
                  TRACKING_ICON_ANCHOR_LABELS[
                    getTrackingIconAnchor(currentIcon.anchor)
                  ]
                }
              </p>
            )}
            {isUnconfigured ? (
              <p className='text-sm text-gray-600'>
                No hay un ícono especial vigente ni un predeterminado habilitado
                para {slot === 'destination' ? 'el destino' : 'el repartidor'}.
              </p>
            ) : isDefault ? (
              <p className='text-sm text-gray-600'>
                Se usa cuando no hay un ícono especial vigente.
              </p>
            ) : (
              <dl className='flex flex-wrap gap-x-8 gap-y-3 text-sm'>
                <div>
                  <dt className='text-gray-500'>Vigente desde</dt>
                  <dd className='mt-1 font-medium text-gray-800'>
                    {currentIcon.dateFrom
                      ? formatDate(currentIcon.dateFrom)
                      : 'Sin fecha de inicio'}
                  </dd>
                </div>
                <div>
                  <dt className='text-gray-500'>Vigente hasta</dt>
                  <dd className='mt-1 font-medium text-gray-800'>
                    {currentIcon.dateTo
                      ? formatDate(currentIcon.dateTo)
                      : 'Sin fecha de fin'}
                  </dd>
                </div>
              </dl>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default CurrentTrackingIconCard;
